import React, { useEffect, FC } from 'react';
import FroalaEditor from 'froala-editor';
import { AlertMessage } from '../../../shared/types';
import Editor, { EditorProps } from './Editor';
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { themeVars } from '@mds/ui';
const customOptions = {
    fontSizeSelection: true,
    fontSizeDefaultSelection: '14',
    lineHeights: {
        Default: '',
        Small: '1.2',
        Medium: '1.5',
        Big: '2',
    },
    toolbarButtons: {
        moreParagraph: {
            align: 'left',
            buttons: [
                'paragraphFormat',
                'formatOL',
                'formatUL',
                'lineHeight',
                'outdent',
                'indent',
                'quote',
            ],
            buttonsVisible: 1,
        },
        moreText: {
            buttons: [
                'bold',
                'italic',
                'underline',
                'fontSize',
                'textColor',
                'clearFormatting',
            ],
            align: 'left',
            buttonsVisible: 0,
        },
        align: {
            align: 'left',
            buttons: ['alignLeft', 'alignCenter', 'alignRight'],
        },
        Register: {
            align: 'left',
            buttons: ['promotionalActionButton'],
        },
        moreRich: {
            align: 'left',
            buttons: ['insertLink', 'insertImage', 'insertVideo', 'embedly'],
            buttonsVisible: 0,
        },
        moreMisc: {
            align: 'right',
            buttons: ['undo', 'redo'],
            buttonsVisible: 2,
        },
    },
    events: {
        'commands.after': function (command: string) {
            const selectedElement = (this as any).selection.element();
            if (selectedElement && command === 'fontSize') {
                selectedElement.style = selectedElement.style.cssText.trim();
            }
        },
    },
};

const promotionEditorSx: SxProps<Theme> = (theme: Theme) => ({
    '& .promotional-action-button': {
        display: 'inline-block',
        pointerEvents: 'none',
        color: `${themeVars(theme).palette.primary.contrastText} !important`,
        backgroundColor: `${themeVars(theme).palette.primary.main} !important`,
        padding: theme.spacing(1.5, 3),
        fontSize: theme.typography.body1.fontSize ?? '1rem',
        letterSpacing: '0.02em',
        textTransform: 'none',
        boxShadow: 'none',
        lineHeight: theme.typography.body1.lineHeight ?? 1.5,
        fontWeight: 600,
        borderWidth: 0,
        '& span': {
            fontSize: 'inherit !important',
            color: 'inherit !important',
            lineHeight: 'inherit !important',
            letterSpacing: '0.02em !important',
        },
        '& em': {
            fontStyle: 'normal !important',
        },
        '& u': {
            textDecoration: 'none !important',
        },
        '& strong': {
            fontWeight: '600 !important',
        },
    },
    '& p, & li': {
        lineHeight: '1.4',
    },
});

interface PromotionEditorProps extends EditorProps {
    setPageMessage?: (arg: AlertMessage) => void;
}

const PromotionEditor: FC<PromotionEditorProps> = ({
    initialHtml,
    onChange,
    errorMessage,
    setPageMessage,
}) => {
    // Set promotional content action icon
    useEffect(() => {
        //Define an icon template
        FroalaEditor.DefineIconTemplate(
            'custom_svg',
            '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">[PATH]</svg>'
        );
        // Define an icon
        FroalaEditor.DefineIcon('promotionalActionIcon', {
            PATH:
                '<path d="M17.1593 20.875L12.8921 16.6079L10.875 19.75L8.625 10.75L17.625 13L14.4829 15.0171L18.75 19.2843L17.1593 20.875Z" />\n' +
                '<path d="M7.5 15.25H4.125C3.50288 15.25 3 14.746 3 14.125V5.125C3 4.504 3.50288 4 4.125 4H19.875C20.4971 4 21 4.504 21 5.125V13H18.75V6.25H5.25V13H7.5V15.25Z" />\n',
            template: 'custom_svg',
        });
    }, []);

    // Set promotional content action toolbar button
    useEffect(() => {
        // Define a dropdown button
        FroalaEditor.RegisterCommand('promotionalActionButton', {
            title: 'Insert Register Interest Button',
            icon: 'promotionalActionIcon',
            focus: true,
            refreshAfterCallback: true,
            callback: function () {
                this.html?.insert(
                    '<button type="button" class="promotional-action-button"><span>Register interest</span></button>&nbsp;'
                );
            },
        });
    }, [setPageMessage]);

    return (
        <Box sx={promotionEditorSx}>
            <Editor
                initialHtml={initialHtml}
                onChange={onChange}
                errorMessage={errorMessage}
                customOptions={customOptions}
            />
        </Box>
    );
};

export default PromotionEditor;
